import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../../context/AuthContext';
import { getPost, deletePost, getAttachmentUrl } from '../../api/news';
import NewsPostDialog from './NewsPostDialog';
import { createMarkdownComponents } from '../common/MarkdownComponents';

const Attachments = ({ attachments, postId }) => {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-3 p-3 bg-gray-50 dark:bg-neutral-800 rounded-md">
      <h3 className="text-lg font-semibold mb-2">Attachments</h3>
      {attachments.map(attachment => (
        <div key={attachment.id} className="flex items-center mb-1">
          <a 
            href={getAttachmentUrl(postId, attachment.filename)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-blue-600 hover:underline"
          >
            {/* Use two images with Tailwind's dark utility */}
            <img className="w-5 h-5 mr-2 dark:hidden" src="/icons/file.svg" alt="File icon" />
            <img className="w-5 h-5 mr-2 hidden dark:block" src="/icons/file-dark.svg" alt="File icon" />
            <span>{attachment.filename}</span>
          </a>
        </div>
      ))}
    </div>
  );
};

const NewsDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const isAdmin = user?.is_staff || user?.is_superuser;
  const isDarkMode = document.documentElement.classList.contains('dark');
  
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const data = await getPost(postId);
        setPost(data);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load the news post');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId]);
  
  const handleEdit = () => {
    setDialogOpen(true);
  };
  
  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    try {
      await deletePost(postId);
      navigate('/news');
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete the post');
    }
  };
  
  const handleDialogClose = (refresh = false) => {
    setDialogOpen(false);
    if (refresh) {
      setLoading(true);
      getPost(postId)
        .then(data => setPost(data))
        .catch(err => {
          console.error('Error reloading post:', err);
          setError('Failed to reload the post');
        })
        .finally(() => setLoading(false));
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex justify-center items-center min-h-[60vh]">
          <svg className="w-10 h-10 text-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </div>
    );
  }
  
  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <button 
          onClick={() => navigate('/news')}
          className="flex items-center mb-4 text-blue-600 hover:underline"
        >
          <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to News
        </button>
        <div className="p-4 bg-red-100 text-red-700 rounded-md">
          {error || 'News post not found'}
        </div>
      </div>
    );
  }
  
  const formattedDate = format(new Date(post.published_at), 'MMMM d, yyyy h:mm a');
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <button 
        onClick={() => navigate('/news')}
        className="flex items-center mb-4 text-blue-600 hover:underline"
      >
        <svg className="w-5 h-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to News
      </button>
      
      <div className="flex flex-col sm:flex-row justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {post.title}
            {post.is_pinned && (
              <svg className="inline-block ml-2 w-5 h-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 13V7a4 4 0 018 0v6a4 4 0 01-8 0z" />
                <path fillRule="evenodd" d="M6 3a2 2 0 00-2 2v1h10V5a2 2 0 00-2-2H6z" clipRule="evenodd" />
              </svg>
            )}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            {post.author_name ? `Posted by ${post.author_name} • ` : ''}
            {formattedDate}
          </p>
          <div className="mb-4">
            {post.roles.map(role => (
              <span 
                key={role.id} 
                className="inline-block px-2 py-1 mr-2 mb-2 bg-gray-200 dark:bg-gray-700 text-xs font-medium rounded"
              >
                {role.name}
              </span>
            ))}
          </div>
        </div>
        {isAdmin && (
          <div className="flex space-x-2">
            <button 
              onClick={handleEdit}
              className="p-2 text-blue-600 hover:text-blue-800"
              title="Edit Post"
            >
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4h6a2 2 0 012 2v6m-3 5l-7 7H4a2 2 0 01-2-2v-4l7-7" />
              </svg>
            </button>
            <button 
              onClick={() => setDeleteDialogOpen(true)}
              className="p-2 text-red-600 hover:text-red-800"
              title="Delete Post"
            >
              <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
      
      <hr className="my-4 border-gray-300 dark:border-gray-600" />

      <div className="prose prose-sm sm:prose lg:prose-lg dark:prose-invert max-w-full">
        <ReactMarkdown
          components={createMarkdownComponents(isDarkMode)}
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
        >
          {post.content}
        </ReactMarkdown>
      </div>
      
      <Attachments attachments={post.attachments} postId={post.id} />
      
      {/* Edit Dialog */}
      <NewsPostDialog 
        open={dialogOpen}
        onClose={handleDialogClose}
        post={post}
      />
      
      {/* Delete Confirmation Modal */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div 
            className="absolute inset-0 bg-black opacity-50"
            onClick={() => setDeleteDialogOpen(false)}
          ></div>
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Confirm Deletion</h3>
            <p className="mb-6 text-gray-700 dark:text-gray-300">
              Are you sure you want to delete this news post? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button 
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 text-sm text-blue-600 hover:underline"
              >
                Cancel
              </button>
              <button 
                onClick={handleDelete}
                className="px-4 py-2 text-sm text-red-600 border border-red-600 rounded hover:bg-red-600 hover:text-white transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsDetail;